import { useEffect, useState, type ChangeEvent } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuthShell } from "@/components/AuthForm";
import { ConfirmDialog } from "@/components/settings/ConfirmDialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, type BillingMe, type User } from "@/lib/api";
import { goToStripe, mxn, openPortal } from "@/lib/billing";

const PLANS = [{ id: "monthly", label: "Mensual" }] as const;

export default function SubscriptionEndedPage({ user }: { user: User }) {
  const { logout, clear } = useAuth();
  const [b, setB] = useState<BillingMe | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState("");
  const [requested, setRequested] = useState("");
  const [typed, setTyped] = useState("");
  useEffect(() => { api.get<BillingMe>("/billing/me").then((r) => setB(r.data)).catch(() => undefined); }, []);

  const reactivate = async (plan: string) => {
    setBusy(plan);
    try {
      const r = await api.post<{ checkout_url: string }>("/billing/checkout", { plan, origin_url: window.location.origin });
      if (!goToStripe(r.data.checkout_url)) window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const requestCopy = async () => {
    setBusy("copy");
    try { setRequested((await api.post<{ message: string }>("/billing/data-request")).data.message); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const remove = async () => {
    try { await api.post("/settings/delete-account", { email: typed }); clear(); window.location.href = "/login"; }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const pastDue = user.billing?.status === "past_due";
  const fixCard = async () => {
    setBusy("portal");
    try { await openPortal(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  return (
    <AuthShell title={pastDue ? "No pudimos cobrar tu suscripción" : "Tu suscripción terminó"}
      subtitle={pastDue ? "Tu banco rechazó el cobro. Actualiza tu tarjeta para volver a entrar; tu información sigue guardada." : "Tu información sigue guardada; reactívala para volver a verla."}>
      <div className="space-y-3" data-testid="subscription-ended">
        {pastDue && (
          <Button onClick={fixCard} disabled={!!busy} className="h-11 w-full bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="ended-update-card">
            {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Actualizar mi tarjeta"}
          </Button>
        )}
        {!choosing ? (
          <Button onClick={() => setChoosing(true)} variant={pastDue ? "outline" : "default"} className={pastDue ? "h-11 w-full" : "h-11 w-full bg-[#1B2A4A] text-white hover:bg-[#2A3D63]"} data-testid="ended-reactivate">{pastDue ? "Pagar con otra tarjeta" : "Reactivar mi suscripción"}</Button>
        ) : (
          <div className="grid gap-2" data-testid="ended-plans">
            {PLANS.map((p) => (
              <Button key={p.id} onClick={() => reactivate(p.id)} disabled={!!busy || !b} className="h-11 bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid={`ended-plan-${p.id}`}>
                {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : `${p.label} · ${b ? mxn(b.plans.monthly) : ""}`}
              </Button>
            ))}
          </div>
        )}
        {requested ? (
          <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-900" data-testid="ended-copy-requested">{requested}</p>
        ) : (
          <Button variant="outline" onClick={requestCopy} disabled={!!busy} className="h-11 w-full" data-testid="ended-request-copy">
            {busy === "copy" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Solicitar una copia de mis datos"}
          </Button>
        )}
        <ConfirmDialog testId="ended-delete" title="¿Eliminar tu cuenta para siempre?" confirmLabel="Eliminar mi cuenta"
          description="Se borran tus cuentas, movimientos, programados, presupuesto y conversación. No se puede deshacer."
          disabled={typed.trim().toLowerCase() !== user.email.toLowerCase()} onConfirm={remove}
          trigger={<Button variant="outline" onClick={() => setTyped("")} className="h-11 w-full border-rose-300 text-rose-600 hover:bg-rose-50" data-testid="ended-delete-account">Eliminar mi cuenta</Button>}>
          <label htmlFor="ended-delete-email" className="text-sm text-slate-700">Escribe <span className="font-semibold">{user.email}</span> para confirmar</label>
          <Input id="ended-delete-email" value={typed} onChange={(e: ChangeEvent<HTMLInputElement>) => setTyped(e.target.value)} autoComplete="off" className="mt-2 h-11" data-testid="ended-delete-email-input" />
        </ConfirmDialog>
        <button type="button" onClick={logout} className="w-full pt-2 text-center text-sm text-slate-500 hover:underline" data-testid="ended-logout">Cerrar sesión</button>
      </div>
    </AuthShell>
  );
}
